import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, TrendingDown, ChevronLeft, QrCode, X, Maximize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useTheme } from "../context/ThemeContext";
import { vipTextColor, vipBadgeBg } from "../lib/vipColors";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const VIP_CFG = {
  Diamond: { color: "#B9F2FF", bg: "linear-gradient(135deg,#0D1B3E,#162240)", border: "#4FC3F7", cardBg: "linear-gradient(135deg,#0D1B3E 0%,#1a2a5e 50%,#0D1B3E 100%)" },
  Platinum: { color: "#E8E8E8", bg: "linear-gradient(135deg,#2D2D2D,#3A3A3A)", border: "#C0C0C0", cardBg: "linear-gradient(135deg,#2D2D2D 0%,#404040 50%,#2D2D2D 100%)" },
  Gold: { color: "var(--ang-gold)", bg: "linear-gradient(135deg,#2A1F0A,#3D2D0D)", border: "var(--ang-gold)", cardBg: "linear-gradient(135deg,#1E1408 0%,#2E2010 50%,#1E1408 100%)" },
  Silver: { color: "#C0C0C0", bg: "linear-gradient(135deg,#1A1A1A,#252525)", border: "var(--ang-text-muted)", cardBg: "linear-gradient(135deg,#1A1A1A 0%,#252525 50%,#1A1A1A 100%)" },
  Standard: { color: "var(--ang-text-muted)", bg: "#141414", border: "#333", cardBg: "linear-gradient(135deg,#141414,#1F1F1F)" },
};

const VIP_NEXT = { Standard: { next: "Silver", amount: 1 }, Silver: { next: "Gold", amount: 1000000 }, Gold: { next: "Platinum", amount: 5000000 }, Platinum: { next: "Diamond", amount: 15000000 }, Diamond: null };
const TIER_CASHBACK = { Diamond: 4, Platinum: 3, Gold: 2, Silver: 1, Standard: 1 };
import { useSeo } from "../lib/seo";

export default function BonusCard() {
  useSeo({ title: "VIP-карта — ANGIOLINI", noindex: true });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [balance, setBalance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      // allSettled — частичные ошибки не должны обнулять баланс
      const [b, h] = await Promise.allSettled([
        axios.get(`${API}/bonus/balance`, { withCredentials: true }),
        axios.get(`${API}/bonus/history`, { withCredentials: true }),
      ]);
      if (b.status === "fulfilled") setBalance(b.value.data);
      if (h.status === "fulfilled") setHistory(h.value.data);
      setLoading(false);
    };
    load();
  }, []);

  const vipLevel = balance?.vip_level || "Silver";
  const bonusBalance = balance?.balance ?? balance?.bonus_balance ?? 0;
  const totalPurchases = balance?.total_purchases ?? 0;
  const { theme } = useTheme();
  const cfgBase = VIP_CFG[vipLevel] || VIP_CFG.Standard;
  const cfg = { ...cfgBase, color: vipTextColor(vipLevel, theme, cfgBase.color) };
  const tierColor = (lvl, fb) => vipTextColor(lvl, theme, fb);
  // Сама VIP-карта остаётся тёмной в любой теме, поэтому внутри неё
  // используем исходные «ночные» цвета уровня.
  const cardColor = cfgBase.color;
  const cardBorder = cfgBase.border;
  const next = VIP_NEXT[vipLevel];
  const progress = next ? Math.min((totalPurchases / next.amount) * 100, 100) : 100;

  // QR payload format: "angiolini:c:<client_id>". The admin scanner parses this exact format.
  const qrPayload = user?.id ? `angiolini:c:${user.id}` : "";
  const [qrOpen, setQrOpen] = useState(false);

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ padding: "52px 20px 20px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: "var(--ang-text-muted)", cursor: "pointer", padding: "4px" }}>
          <ChevronLeft size={20} />
        </button>
        <h1 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", color: "var(--ang-text)", fontWeight: 400, margin: 0 }}>{t.bonus.title}</h1>
      </div>

      {/* VIP Card */}
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
        style={{ margin: "0 16px 24px" }}>
        <div data-testid="vip-card" style={{ background: cfg.cardBg, borderRadius: "12px", padding: "24px", border: `1px solid ${cardBorder}33`, position: "relative", overflow: "hidden", minHeight: "180px" }}>
          {/* Shine overlay */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(135deg,rgba(255,255,255,0.06) 0%,transparent 50%,rgba(255,255,255,0.06) 100%)", pointerEvents: "none" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "20px" }}>
            <div style={{ fontFamily: "Italiana", fontSize: "22px", color: "var(--ang-gold-2)", letterSpacing: "0.04em" }}>Angiolini</div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "5px" }}>
              <div style={{ background: "rgba(255,255,255,0.08)", border: `1px solid ${cardBorder}55`, borderRadius: "3px", padding: "4px 10px", fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, letterSpacing: "0.15em", color: cardColor, textTransform: "uppercase" }}>{vipLevel}</div>
              <div data-testid="cashback-pct-badge" style={{ background: cardColor + "22", border: `1px solid ${cardColor}`, borderRadius: "3px", padding: "3px 8px", fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, color: cardColor, letterSpacing: "0.06em" }}>
                Кэшбэк {TIER_CASHBACK[vipLevel] || 1}%
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <div data-testid="card-bonus-balance" style={{ fontFamily: "Cormorant Garamond", fontSize: "40px", fontWeight: 600, color: cardColor, lineHeight: 1 }}>{Math.floor(bonusBalance).toLocaleString("ru-RU")}</div>
              <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-card-muted)", letterSpacing: "0.15em", marginTop: "4px" }}>{t.bonus.bonuses} · доступно</div>
              {(balance?.locked || 0) > 0 && (
                <div data-testid="card-locked-hint" style={{ fontFamily: "Manrope", fontSize: "11px", color: cardColor, marginTop: "6px", opacity: 0.85, display: "inline-flex", alignItems: "center", gap: "4px", background: "rgba(255,255,255,0.08)", border: `1px dashed ${cardColor}55`, padding: "3px 8px", borderRadius: "3px" }}>
                  + {Math.floor(balance.locked).toLocaleString("ru-RU")} заморожено
                </div>
              )}
              <div style={{ fontFamily: "Manrope", fontSize: "13px", color: "var(--ang-card-text)", marginTop: "12px" }}>{user?.name}</div>
              <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-card-muted)" }}>{user?.phone}</div>
            </div>
            <div>
              <button data-testid="open-qr-btn" onClick={() => setQrOpen(true)} aria-label="Показать QR-код"
                style={{ background: "white", padding: "8px", borderRadius: "6px", cursor: "pointer", border: "none", position: "relative", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}>
                {qrPayload ? (
                  <QRCodeSVG value={qrPayload} size={80} bgColor="#ffffff" fgColor="#0A0A0A" level="M" />
                ) : <div style={{ width: 80, height: 80, background: "#f3f4f6" }} />}
                <div style={{ position: "absolute", top: "4px", right: "4px", background: cardColor, color: "#0A0A0A", borderRadius: "50%", width: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>
                  <Maximize2 size={11} strokeWidth={3} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Show QR button (alternative entry) */}
      <div style={{ padding: "0 16px 20px" }}>
        <button data-testid="show-qr-card-btn" onClick={() => setQrOpen(true)}
          style={{ width: "100%", background: "var(--ang-surface-2)", border: "1px solid var(--ang-gold-line)", borderRadius: "8px", padding: "14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", color: "var(--ang-gold)", fontFamily: "Manrope", fontSize: "13px", fontWeight: 600, letterSpacing: "0.05em" }}>
          <QrCode size={16} /> Показать QR-карту менеджеру
        </button>
      </div>

      {/* VIP Progress */}
      <div style={{ margin: "0 16px 24px", background: "var(--ang-surface)", borderRadius: "8px", padding: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
          <span style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-muted)" }}>{t.bonus.progress} {next?.next || t.bonus.max}</span>
          <span style={{ fontFamily: "Manrope", fontSize: "12px", color: cfg.color }}>{Math.round(progress)}%</span>
        </div>
        <div style={{ background: "var(--ang-border)", borderRadius: "4px", height: "6px", marginBottom: "10px" }}>
          <div style={{ width: `${progress}%`, height: "100%", background: `linear-gradient(90deg,${cfg.color},${cfg.color}88)`, borderRadius: "4px", transition: "width 1.2s ease" }} />
        </div>
        {next ? (
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)" }}>
            {((next.amount - totalPurchases) > 0 ? (next.amount - totalPurchases) : 0).toLocaleString("ru-RU")} {t.bonus.toStatus} {next.next}
          </div>
        ) : (
          <div style={{ fontFamily: "Manrope", fontSize: "11px", color: cfg.color }}>{t.bonus.maxReached}</div>
        )}
      </div>

      {/* Balance breakdown — full transparency */}
      <div data-testid="balance-breakdown" style={{ margin: "0 16px 24px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
        <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "12px", border: "1px solid var(--ang-gold-soft)" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>Доступно</div>
          <div data-testid="balance-available" style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-gold)", fontWeight: 600, lineHeight: 1 }}>
            {Math.floor(balance?.balance || 0).toLocaleString("ru-RU")}
          </div>
        </div>
        <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "12px", border: "1px dashed var(--ang-gold-line)" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>Заморожено</div>
          <div data-testid="balance-locked" style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text-soft)", fontWeight: 600, lineHeight: 1 }}>
            {Math.floor(balance?.locked || 0).toLocaleString("ru-RU")}
          </div>
        </div>
        <div style={{ background: "var(--ang-surface)", borderRadius: "8px", padding: "12px", border: (balance?.expiring_soon || 0) > 0 ? "1px solid rgba(239,68,68,0.45)" : "1px solid var(--ang-border)" }}>
          <div style={{ fontFamily: "Manrope", fontSize: "9px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>Сгорит ≤7 дн.</div>
          <div data-testid="balance-expiring" style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: (balance?.expiring_soon || 0) > 0 ? "#fca5a5" : "var(--ang-text-muted)", fontWeight: 600, lineHeight: 1 }}>
            {Math.floor(balance?.expiring_soon || 0).toLocaleString("ru-RU")}
          </div>
        </div>
      </div>

      {/* VIP Levels */}
      <div style={{ margin: "0 16px 24px" }}>
        <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "12px" }}>{t.bonus.levels}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
          {[["Silver", `${t.bonus.from} 500 000 ₸`, tierColor("Silver", "#C0C0C0")], ["Gold", `${t.bonus.from} 1 500 000 ₸`, tierColor("Gold", "var(--ang-gold)")], ["Platinum", `${t.bonus.from} 3 000 000 ₸`, tierColor("Platinum", "#E8E8E8")], ["Diamond", `${t.bonus.from} 5 000 000 ₸`, tierColor("Diamond", "#B9F2FF")]].map(([lvl, desc, color]) => (
            <div key={lvl} data-testid={`vip-level-${lvl.toLowerCase()}`}
              style={{ background: lvl === vipLevel ? "var(--ang-gold-soft)" : "var(--ang-surface)", border: `1px solid ${lvl === vipLevel ? color : "var(--ang-border)"}`, borderRadius: "6px", padding: "12px" }}>
              <div style={{ fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: color, letterSpacing: "0.1em", marginBottom: "4px" }}>{lvl}</div>
              <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)" }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Transaction History */}
      <div style={{ margin: "0 16px 24px" }}>
        <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "20px", color: "var(--ang-text)", fontWeight: 400, marginBottom: "12px" }}>{t.bonus.history}</h3>
        {loading ? (
          <div style={{ textAlign: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "13px", padding: "24px" }}>{t.common.loading}</div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope", fontSize: "13px", padding: "24px" }}>{t.bonus.empty}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {history.map((txn) => {
              const positive = (txn.amount || 0) > 0;
              const typeLabels = {
                welcome: "Приветственный",
                cashback: "Кэшбэк",
                tier_cashback: "Кэшбэк",
                purchase_cashback: "Кэшбэк",
                referral_signup: "Реферал",
                referral_purchase: "Кэшбэк с друга",
                referral_inviter: "Реферал",
                referral_invitee: "Бонус другу",
                birthday: "День рождения",
                spend: "Списание",
                spent: "Списание",
                expired: "Истёк",
                admin_adjust: "Корректировка",
                admin_grant: "Начислено админом",
                admin_deduct: "Списано админом",
              };
              const label = typeLabels[txn.type] || txn.type || "Бонус";
              const isLocked = txn.unlock_at && new Date(txn.unlock_at) > new Date();
              const isExpired = txn.status === "expired";
              return (
                <div key={txn.id} data-testid={`txn-${txn.id}`}
                  style={{
                    background: "var(--ang-surface)",
                    borderRadius: "6px",
                    padding: "14px 16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    border: isLocked ? "1px dashed var(--ang-gold-line)" : "1px solid transparent",
                    opacity: isExpired ? 0.55 : 1,
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
                    <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: positive ? "var(--ang-gold-soft)" : "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {positive ? <TrendingUp size={14} style={{ color: "var(--ang-gold)" }} /> : <TrendingDown size={14} style={{ color: "#ef4444" }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "Manrope", fontSize: "12.5px", color: "var(--ang-text-soft)", fontWeight: 600 }}>{label}</span>
                        {isLocked && (
                          <span data-testid="txn-locked-badge" style={{ background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)", color: "var(--ang-gold)", borderRadius: "3px", padding: "1px 6px", fontFamily: "Manrope", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            Заморожено
                          </span>
                        )}
                        {isExpired && (
                          <span style={{ background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.4)", color: "#fca5a5", borderRadius: "3px", padding: "1px 6px", fontFamily: "Manrope", fontSize: "9px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            Истёк
                          </span>
                        )}
                      </div>
                      {txn.description && (
                        <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)", lineHeight: 1.3, marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {txn.description}
                        </div>
                      )}
                      <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-faint)", marginTop: "2px" }}>
                        {txn.created_at ? new Date(txn.created_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        {isLocked && (
                          <span style={{ marginLeft: "8px", color: "var(--ang-gold-strong)" }}>
                            · разблок. {new Date(txn.unlock_at).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: "Manrope", fontSize: "14px", fontWeight: 700, color: positive ? "var(--ang-gold)" : "#ef4444", whiteSpace: "nowrap", marginLeft: "10px" }}>
                    {positive ? "+" : ""}{Math.floor(txn.amount || 0).toLocaleString("ru-RU")}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Fullscreen QR modal */}
      <AnimatePresence>
        {qrOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            data-testid="qr-modal"
            onClick={() => setQrOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(8px)", zIndex: 1000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "20px" }}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              style={{ position: "relative", maxWidth: "420px", width: "100%" }}>
              <button data-testid="close-qr-btn" onClick={() => setQrOpen(false)} aria-label="Закрыть"
                style={{ position: "absolute", top: "-44px", right: "0px", background: "var(--ang-border)", border: "1px solid var(--ang-border)", borderRadius: "50%", width: "34px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "var(--ang-text)" }}>
                <X size={18} />
              </button>

              <div style={{ background: "white", borderRadius: "16px", padding: "32px 24px", textAlign: "center", boxShadow: "0 16px 60px rgba(0,0,0,0.5)" }}>
                <div style={{ fontFamily: "Italiana", fontSize: "26px", color: "var(--ang-bg)", marginBottom: "4px" }}>Angiolini</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-text-muted)", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "24px" }}>VIP Card</div>

                <div style={{ display: "flex", justifyContent: "center", padding: "12px", background: "white", borderRadius: "8px", border: "1px solid #f3f4f6", marginBottom: "20px" }}>
                  {qrPayload && <QRCodeSVG value={qrPayload} size={260} level="M" bgColor="#ffffff" fgColor="#0A0A0A" />}
                </div>

                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", color: "var(--ang-bg)", fontWeight: 500 }}>{user?.name}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-muted)", marginTop: "2px" }}>{user?.phone}</div>

                <div style={{ display: "inline-block", marginTop: "16px", padding: "5px 12px", background: cfg.color + "20", border: `1px solid ${cfg.color}`, borderRadius: "20px", fontFamily: "Manrope", fontSize: "10px", fontWeight: 700, color: cfg.color === "#E8E8E8" ? "#666" : cfg.color, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                  {vipLevel}
                </div>

                <div style={{ marginTop: "20px", padding: "12px", background: "#FDFBF7", border: "1px solid #f3f4f6", borderRadius: "6px", fontFamily: "Manrope", fontSize: "10px", color: "#666", lineHeight: 1.5 }}>
                  Покажите этот QR-код менеджеру для идентификации,<br /> начисления бонусов и записи покупки.
                </div>

                <div style={{ marginTop: "12px", fontFamily: "monospace", fontSize: "11px", color: "#9ca3af" }}>
                  ID: {user?.id?.slice(-8) || "—"}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
